/**
 * Undo/redo stacks over whole snapshots of what is being edited. Pure, so the hook that drives an
 * editor with them (useEditHistory) only has to decide when to call them.
 */

export const HISTORY_LIMIT = 100;

export const EMPTY_HISTORY = { past: [], future: [] };

/** Records the state an edit is about to replace. A new edit makes whatever was undone unreachable. */
export const pushHistory = (history, snapshot, limit = HISTORY_LIMIT) => ({
  past: [...history.past, snapshot].slice(-limit),
  future: [],
});

/** The snapshot to go back to, with `present` kept for redo — or null with nothing to undo. */
export const undoHistory = (history, present) => {
  if (!history.past.length) return null;

  return {
    snapshot: history.past[history.past.length - 1],
    history: { past: history.past.slice(0, -1), future: [present, ...history.future] },
  };
};

/** The snapshot to go forward to, with `present` kept for undo — or null with nothing to redo. */
export const redoHistory = (history, present) => {
  if (!history.future.length) return null;

  const [snapshot, ...future] = history.future;

  return { snapshot, history: { past: [...history.past, present], future } };
};

/**
 * Undo is Cmd+Z (Ctrl+Z off a Mac); redo is Cmd+Shift+Z or Cmd+Y. Returns 'undo', 'redo', or null
 * for any other key.
 */
export const getHistoryShortcut = (event) => {
  if (!(event.metaKey || event.ctrlKey) || event.altKey) return null;

  const key = event.key.toLowerCase();

  if (key === 'z') return event.shiftKey ? 'redo' : 'undo';
  if (key === 'y' && !event.shiftKey) return 'redo';

  return null;
};

export const TYPING_RUN_MS = 1000;

const countLineBreaks = (text) => (text.match(/\n/g) || []).length;

/**
 * Whether a keystroke joins the typing run before it, so the run undoes as one step. A pause, or a
 * new line, starts the next one.
 */
export const continuesTypingRun = ({ lastTypedAt, now, previous, next }, windowMs = TYPING_RUN_MS) => {
  if (lastTypedAt == null || now - lastTypedAt >= windowMs) return false;

  return countLineBreaks(next) <= countLineBreaks(previous);
};
